/**
 * ADMIN MODULE — SEO Rank data access.
 * Whitelisted reads/writes over the seo_* tables (RLS pins them to platform
 * admins). Components call these instead of touching supabase directly, mirroring
 * flywheelService. Errors are logged here and surfaced generic.
 *
 * Tables (migration 0040): seo_keywords, seo_audits, seo_articles,
 * seo_writing_lessons, seo_api_usage, seo_settings.
 * Extended (migration 0052 — SEO Content Engine): seo_clusters, seo_briefs,
 * seo_performance; seo_keywords gains cluster_id + intent_score; seo_articles
 * gains brief_id + quality_score + body_md/body_html; both status enums widened.
 */

import { supabase } from "../../../shared/utils/supabaseClient.js";

// Published articles live on the MARKETING site, not this app. seo_articles.blog_path
// is stored root-relative ("/blog/slug.html"), which resolves against app.brandcommand.app
// and 404s — every "Open live page" link was broken. Resolve it against the marketing
// origin instead. Override with VITE_MARKETING_URL for a local site preview.
export const MARKETING_URL = (
  import.meta.env.VITE_MARKETING_URL || "https://brandcommand.app"
).replace(/\/$/, "");

export const blogUrl = (blogPath) =>
  !blogPath ? null : /^https?:/i.test(blogPath) ? blogPath : `${MARKETING_URL}${blogPath}`;

// 'needs_review' (0052) sits between draft and approved — rubric-gated articles
// land there. 'briefed' (0052) is the keyword stage after a brief is generated.
export const ARTICLE_STATUSES = ["draft", "needs_review", "approved", "published", "rejected"];
export const KEYWORD_STATUSES = ["new", "approved", "briefed", "used", "archived"];
// Brief lifecycle (migration 0054 adds 'held'). 'ready' = the Article Writer may
// pick it; 'held' = postponed (stays visible, but excluded from auto-pick).
export const BRIEF_STATUSES = ["draft", "ready", "held"];

function fail(where, error, msg) {
  console.error(`[seoService.${where}]`, error);
  throw new Error(msg);
}

/* ───────────────────────────── Clusters ───────────────────────────── */
// Pillar topics (migration 0052). The strategic backbone — discovery runs
// against active clusters. Seeded by migration; edited here.
export async function listClusters() {
  const { data, error } = await supabase
    .from("seo_clusters")
    .select("id, pillar_topic, intent, priority, status, notes, created_at")
    .order("status", { ascending: true })
    .order("priority", { ascending: false });
  if (error) fail("listClusters", error, "Could not load clusters.");
  return data ?? [];
}

/* ───────────────────────────── Keywords ───────────────────────────── */
export async function listKeywords() {
  const { data, error } = await supabase
    .from("seo_keywords")
    .select("id, seed_keyword, keyword, volume, difficulty, cpc, opportunity_score, intent_score, cluster_id, paa_questions, status, created_at")
    // Archived keywords stay in the table (dedupe reads the full table server-side)
    // but never show in the queue — archiving is how staff make a row disappear.
    .neq("status", "archived")
    // intent_score is the primary signal (buyer intent > raw volume); fall back
    // to opportunity_score for rows discovered before intent scoring existed.
    .order("intent_score", { ascending: false, nullsFirst: false })
    .order("opportunity_score", { ascending: false, nullsFirst: false })
    .limit(500);
  if (error) fail("listKeywords", error, "Could not load keywords.");
  return data ?? [];
}

export async function setKeywordStatus(id, status) {
  if (!KEYWORD_STATUSES.includes(status)) throw new Error("Invalid keyword status.");
  const { error } = await supabase.from("seo_keywords").update({ status }).eq("id", id);
  if (error) fail("setKeywordStatus", error, "Could not update the keyword.");
}

/* ───────────────────────────── Audits ─────────────────────────────── */
export async function listAudits() {
  const { data, error } = await supabase
    .from("seo_audits")
    .select("id, url, keyword, audit_results, recommendations, generated_tags, created_at")
    .order("created_at", { ascending: false })
    .limit(200);
  if (error) fail("listAudits", error, "Could not load audits.");
  return data ?? [];
}

/* ───────────────────────────── Briefs ─────────────────────────────── */
// Content plans (migration 0052). One per approved keyword; the Article Writer
// writes FROM a brief. Surfaced here so staff can pick which brief becomes an
// article, postpone one (status → 'held'), or delete it.
export async function listBriefs() {
  const { data, error } = await supabase
    .from("seo_briefs")
    .select("id, keyword_id, working_title, outline, internal_links, expertise_prompts, target_word_count, status, created_at")
    .order("created_at", { ascending: false })
    .limit(500);
  if (error) fail("listBriefs", error, "Could not load briefs.");
  return data ?? [];
}

export async function setBriefStatus(id, status) {
  if (!BRIEF_STATUSES.includes(status)) throw new Error("Invalid brief status.");
  const { error } = await supabase.from("seo_briefs").update({ status }).eq("id", id);
  if (error) fail("setBriefStatus", error, "Could not update the brief.");
}

// Hard-delete a brief. Articles reference brief_id with ON DELETE SET NULL, so any
// already-written article is preserved (it just loses the back-link).
export async function deleteBrief(id) {
  const { error } = await supabase.from("seo_briefs").delete().eq("id", id);
  if (error) fail("deleteBrief", error, "Could not delete the brief.");
}

/* ──────────────────────────── Articles ────────────────────────────── */
export async function listArticles() {
  const { data, error } = await supabase
    .from("seo_articles")
    .select("id, keyword_id, brief_id, title, slug, meta_description, target_keyword, word_count, html_content, body_md, body_html, json_meta, quality_score, status, blog_path, published_at, created_at")
    .order("created_at", { ascending: false });
  if (error) fail("listArticles", error, "Could not load articles.");
  return data ?? [];
}

export async function setArticleStatus(id, status) {
  if (!ARTICLE_STATUSES.includes(status)) throw new Error("Invalid article status.");
  const { error } = await supabase.from("seo_articles").update({ status }).eq("id", id);
  if (error) fail("setArticleStatus", error, "Could not update the article.");
}

export async function updateArticleContent(id, html_content) {
  const { error } = await supabase
    .from("seo_articles")
    .update({ html_content: String(html_content ?? "").slice(0, 200000) })
    .eq("id", id);
  if (error) fail("updateArticleContent", error, "Could not save the article.");
}

// Records a human edit as a lesson the writer agent learns from. Best-effort.
export async function recordArticleLesson({ article_id, original_text, edited_text, note }) {
  const { error } = await supabase.from("seo_writing_lessons").insert({
    article_id: article_id ?? null,
    original_text: String(original_text ?? "").slice(0, 12000),
    edited_text: String(edited_text ?? "").slice(0, 12000),
    note: note ? String(note).slice(0, 2000) : null,
  });
  if (error) console.warn("[seoService.recordArticleLesson]", error);
}

/* ──────────────────────── Rank performance ────────────────────────── */
// Rank snapshots over time (migration 0052). Returned oldest-first so the UI can
// draw a series per article/keyword.
export async function listPerformance() {
  const { data, error } = await supabase
    .from("seo_performance")
    .select("id, article_id, keyword, position, serp_features, snapshot_date")
    .order("snapshot_date", { ascending: true })
    .limit(5000);
  if (error) fail("listPerformance", error, "Could not load rank performance.");
  return data ?? [];
}

/* ────────────────────────── Cost meter ────────────────────────────── */
// Today's + this week's DataForSEO spend (USD), from seo_api_usage.
export async function getCostMeter() {
  const now = new Date();
  const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString();
  const weekStart = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();

  const sumSince = async (iso) => {
    const { data, error } = await supabase
      .from("seo_api_usage")
      .select("cost, created_at")
      .gte("created_at", iso)
      .limit(5000);
    if (error) { console.error("[seoService.getCostMeter]", error); return null; }
    return (data ?? []).reduce((acc, r) => acc + Number(r.cost || 0), 0);
  };

  const [today, week] = await Promise.all([sumSince(dayStart), sumSince(weekStart)]);
  return { today, week };
}

/* ────────────────────────── Settings ──────────────────────────────── */
const SETTINGS_FIELDS = new Set([
  "research_enabled", "audit_enabled", "writer_enabled", "publisher_enabled",
  "articles_per_week", "keyword_target", "min_volume", "max_difficulty",
  "daily_cost_cap", "auto_generate", "auto_generate_count",
]);

export async function getSettings() {
  const { data, error } = await supabase
    .from("seo_settings")
    .select("id, research_enabled, audit_enabled, writer_enabled, publisher_enabled, articles_per_week, keyword_target, min_volume, max_difficulty, daily_cost_cap, auto_generate, auto_generate_count, last_generated_at, updated_at")
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) fail("getSettings", error, "Could not load settings.");
  return data ?? null;
}

const clampInt = (v, lo, hi, dflt) => Math.max(lo, Math.min(hi, Math.round(Number(v)) || dflt));
const clampNum = (v, lo, hi, dflt) => Math.max(lo, Math.min(hi, Number(v) || dflt));

export async function updateSettings(id, patch) {
  const out = {};
  for (const k of SETTINGS_FIELDS) {
    if (Object.prototype.hasOwnProperty.call(patch, k)) out[k] = patch[k];
  }
  if ("articles_per_week" in out) out.articles_per_week = clampInt(out.articles_per_week, 1, 21, 3);
  if ("keyword_target" in out) out.keyword_target = clampInt(out.keyword_target, 5, 100, 25);
  if ("min_volume" in out) out.min_volume = clampInt(out.min_volume, 0, 1000000, 50);
  if ("max_difficulty" in out) out.max_difficulty = clampNum(out.max_difficulty, 0, 100, 60);
  if ("daily_cost_cap" in out) out.daily_cost_cap = clampNum(out.daily_cost_cap, 0, 1000, 5);
  if ("auto_generate_count" in out) out.auto_generate_count = clampInt(out.auto_generate_count, 1, 10, 3);
  for (const b of ["research_enabled", "audit_enabled", "writer_enabled", "publisher_enabled", "auto_generate"]) {
    if (b in out) out[b] = !!out[b];
  }
  if (Object.keys(out).length === 0) return;
  const { error } = await supabase.from("seo_settings").update(out).eq("id", id);
  if (error) fail("updateSettings", error, "Could not save settings.");
}
